import { ImageIcon, Check, Wand2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ProfilePhotoPreset } from "@/config/profilePhotoPresets";

interface ProfilePhotoPresetGridProps {
  presets: ProfilePhotoPreset[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function ProfilePhotoPresetGrid({
  presets,
  selectedId,
  onSelect,
}: ProfilePhotoPresetGridProps) {
  const { t } = useLanguage();

  const presetName = (preset: ProfilePhotoPreset): string => {
    const presets = t.profilePhoto.presets as Record<string, string>;
    return presets[preset.nameKey] ?? preset.nameKey;
  };

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.profilePhoto.pickPreset}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t.profilePhoto.pickPresetHint}
        </p>
      </div>
      <div className="flex md:grid md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {presets.map((preset) => {
          const isActive = selectedId === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onSelect(preset.id)}
              className={cn(
                "group relative aspect-[3/4] rounded-2xl overflow-hidden border-2 bg-card transition-all duration-200 flex-shrink-0 w-[40vw] md:w-auto snap-start",
                isActive
                  ? "border-brand ring-2 ring-brand shadow-md"
                  : "border-border hover:border-brand/40 hover:shadow-sm",
              )}
            >
              {preset.isCustom ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-brand/25 via-brand/10 to-brand-secondary/20">
                  <div className="absolute inset-2.5 rounded-xl border-2 border-dashed border-brand/40" />
                  <div className="relative z-10 w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center shadow-md">
                    <Wand2 className="w-6 h-6" />
                  </div>
                  <span className="relative z-10 px-2 text-center text-[11px] leading-tight font-medium text-foreground/70">
                    {t.profilePhoto.customCardSubtitle}
                  </span>
                </div>
              ) : preset.previewImage ? (
                <img
                  src={preset.previewImage}
                  alt={presetName(preset)}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
                  <ImageIcon className="w-10 h-10 text-muted-foreground/40" />
                </div>
              )}

              <div
                className={cn(
                  "absolute inset-x-0 bottom-0 p-3",
                  preset.isCustom
                    ? "bg-gradient-to-t from-brand/30 to-transparent"
                    : "bg-gradient-to-t from-black/70 via-black/30 to-transparent",
                )}
              >
                <span
                  className={cn(
                    "text-sm font-semibold",
                    preset.isCustom ? "text-foreground" : "text-white",
                  )}
                >
                  {presetName(preset)}
                </span>
              </div>

              {isActive && (
                <div className="absolute top-2 end-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
